import CloseIcon from '@mui/icons-material/Close'
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
} from '@mui/material'
import type {SxProps} from '@mui/material'
import type {Theme} from '@mui/material/styles'
import {StrictMode, useEffect, useRef} from 'react'
import type {BaseSyntheticEvent, FC, ReactElement, ReactNode, SyntheticEvent} from 'react'
import {useCustomTranslation} from '@hooks/useCustomTranslation'
import {useOverlayMount} from '@hooks/useOverlayMount'

type Props = {
  children?: ReactElement
  footerAction?: ReactElement
  onClose: (event?: SyntheticEvent | BaseSyntheticEvent) => void
  title: ReactNode
  open: boolean
  wrappers?: boolean
  handleCloseFunctions?: () => void
  closeButtonLabel?: string
  maxWidth?: string
  width?: string
  height?: string
  dataCy?: string
  bgColor?: string
  removeBgImage?: boolean
  sx?: SxProps<Theme>
  headerAction?: ReactElement
  hideCancelButton?: boolean
  // Called once the dialog has fully closed and unmounted.
  onExited?: () => void
}

const BasicModal: FC<Props> = ({
  children,
  onClose,
  title,
  open,
  footerAction,
  wrappers = true,
  handleCloseFunctions,
  closeButtonLabel,
  maxWidth,
  width = 'auto',
  height = 'auto',
  dataCy = 'dialog-basic-modal',
  bgColor,
  removeBgImage = false,
  sx,
  headerAction,
  hideCancelButton = false,
  onExited,
}): ReactElement | null => {
  const [t] = useCustomTranslation('common')
  // MUI only unmounts a Dialog when its exit transition reports back, and that
  // report is lost whenever the tree re-renders mid-exit — leaving a
  // full-viewport Modal root over the page that eats every click and keeps the
  // body scroll lock. Own the unmount instead (see useOverlayMount, AB#6069;
  // same footgun as AB#5750 / AB#5784).
  const {mounted, onTransitionExited} = useOverlayMount(open)

  // Fires on unmount, so it also covers the fallback path when MUI's exit report is lost.
  const wasMountedRef = useRef(mounted)
  useEffect(() => {
    if (wasMountedRef.current && !mounted) {
      onExited?.()
    }
    wasMountedRef.current = mounted
  }, [mounted, onExited])

  const handleClose = (): void => {
    if (handleCloseFunctions) {
      handleCloseFunctions()
    }
    onClose()
  }

  /*
   * Callers have always written this as `& .MuiPaper-root`, meaning "the
   * dialog's paper". It is still accepted under that name — a dozen screens
   * pass it — but it is applied to `.MuiDialog-paper` below, which is the only
   * Paper they ever meant.
   */
  const {'& .MuiPaper-root': externalPaperSx, ...restSx} = (sx as Record<string, unknown>) || {}

  if (!mounted) {
    return null
  }

  return (
    <StrictMode>
      <Dialog
        onClose={handleClose}
        onTransitionExited={onTransitionExited}
        open={open}
        fullWidth={!!maxWidth}
        maxWidth={maxWidth ? false : 'sm'}
        data-testid="mbai-main-basic-modal"
        data-cy={dataCy}
        sx={{
          /*
           * `.MuiDialog-paper`, NOT `.MuiPaper-root`.
           *
           * A DESCENDANT selector on `.MuiPaper-root` hits every Paper inside
           * the dialog, and MUI builds `Alert`, `Card`, `Accordion` and `Menu`
           * out of Paper — so each of them silently inherited the DIALOG's
           * `width` and `maxWidth`. An Alert in a 320px pane became `width:
           * 96vw` and its text ran off the side, which reads as "the text does
           * not wrap" rather than as a width being inherited from three
           * components up.
           *
           * That bug was found and worked around locally at least three times
           * (a clipped Alert, an Accordion chevron pushed off-screen, and
           * again in the section settings pane) before anyone looked here.
           * `.MuiDialog-paper` is on the dialog's own paper and nothing else,
           * so the workarounds are gone with it.
           */
          '& .MuiDialog-paper': {
            maxWidth: maxWidth || '600px',
            ...(width !== 'auto' && {width}),
            ...(removeBgImage && {backgroundImage: 'none'}),
            ...(bgColor && {bgcolor: bgColor}),
            ...((externalPaperSx as object) || {}),
          },
          '& .MuiDialogActions-root': {
            borderTop: 1,
            borderColor: 'divider',
            ...(bgColor && {bgcolor: bgColor}),
          },
          ...restSx,
        }}
      >
        <Box
          sx={{
            display: 'grid',
            // With a centered headerAction, balance both sides (1fr … 1fr).
            // Without one, don't waste a full column on the close button — give
            // the title the remaining width so long titles truncate near the ✕
            // instead of at ~half width.
            // minmax(0, …) rather than a bare 1fr: a grid track sized 1fr will
            // not shrink below its content's min-content width, so a long title
            // pushed its column wider than its share and ran underneath the
            // centered action instead of truncating at the ✕ as intended.
            gridTemplateColumns: headerAction
              ? 'minmax(0, 1fr) auto minmax(0, 1fr)'
              : 'minmax(0, 1fr) auto auto',
            alignItems: 'center',
            borderBottom: 1,
            borderColor: 'divider',
            gap: 1,
          }}
        >
          <Box sx={{display: 'flex', alignItems: 'center', minWidth: 0}}>
            {/* flexShrink: MUI gives DialogTitle `flex: 0 0 auto`, so `noWrap`
                never got the chance to truncate — the title kept its full
                content width and ran under the centered header action. */}
            <DialogTitle
              noWrap
              data-cy="dialog-title-basic-modal"
              sx={{pr: 0, minWidth: 0, flexShrink: 1}}
            >
              {title}
            </DialogTitle>
          </Box>
          <Box sx={{display: 'flex', justifyContent: 'center'}}>{headerAction}</Box>
          <Box sx={{display: 'flex', justifyContent: 'flex-end'}}>
            <IconButton
              sx={{
                mr: 1,
                mt: 1,
                height: '45px',
                width: '45px',
              }}
              onClick={() => onClose()}
              data-testid="mbai-main-basic-modal-close-icon"
              data-cy="mbai-main-basic-modal-close-icon"
            >
              <CloseIcon sx={{color: 'text.disabled'}} />
            </IconButton>
          </Box>
        </Box>
        {wrappers ? <DialogContent sx={{height}}>{children}</DialogContent> : children}
        {(!hideCancelButton || footerAction) && (
          <DialogActions>
            {footerAction}
            {!hideCancelButton && (
              <Button
                onClick={handleClose}
                name="view-save-update-form-cancel"
                data-testid="mbai-main-basic-modal-close-button"
                data-cy="dialog-cancel-button-basic-modal"
              >
                {closeButtonLabel || t('cancel')}
              </Button>
            )}
          </DialogActions>
        )}
      </Dialog>
    </StrictMode>
  )
}

export default BasicModal
